import { useRef, useState, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { axios } from '@/services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import AGGridMain from '../../../components/tables/AGGrid/AGGridMain';
import { Box, HStack, Tooltip, Text, AlertTitle } from '@chakra-ui/react';
import { Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { Alert, AlertIcon } from '@/components/v4';
import ModalCopy from '@/components/modal/ModalCopy';
import NameField from '@/components/forms/new/formikFormFields/NameField';
import ItemCodeMapping from './edit/ItemCodeMapping';
import CallInclusions from './edit/CallInclusions';
import CallingRates from './edit/CallingRates';
import NumberBlockRates from './edit/NumberBlockRates';
import config from '../../../config.json';
import PlanDetails from './edit/PlanDetails';
import BreadcrumbEntityDropdown from 'components/pickers/BreadcrumbEntityDropdown';
import { Button } from 'components/v4';
import {
    customTabStylesCombineTheme,
    selectedTabStylesCombineTheme,
} from '@/constants';
import ActionMenu from 'components/presentational/ActionMenu';
import { EditIcon } from '@chakra-ui/icons';
import { CopyIcon } from 'components/v4/Icon/CopyIcon';

const Plans = () => {
    const dispatch = useDispatch();
    const tableRef = useRef();
    const { currentPartner, currentWholesaler } = useSelector((state) => ({
        ...state.navigationLists,
    }));
    const { currentScope } = useSelector((state) => ({ ...state.login }));
    const plansUrl =
        currentScope === 40
            ? `/partner/${currentPartner}/plans`
            : currentScope === 60
              ? `/wholesaler/${currentWholesaler}/plans`
              : '/plans/';

    const {
        data: plans = [],
        isLoading,
        isFetching,
        error: plansError,
        refetch: refetchPlans,
    } = useQuery({
        queryKey: ['plans', currentScope],
        queryFn: async () => {
            const res = await axios.get(plansUrl);
            dispatch({ type: 'CHANGE_TABLE_LOADING' });
            const data = res.data || [];
            return data.map(({ wholesaler, partner, ...rest }) => ({
                ...rest,
                wholesaler:
                    wholesaler?.length > 0
                        ? wholesaler
                        : config.notAllocated.table,
                partner:
                    partner?.length > 0 ? partner : config.notAllocated.table,
            }));
        },
    });
    const error = plansError ? 'Error loading plans data' : '';
    // For add/edit form state
    const [plansAction, setPlansAction] = useState('Get'); // 'Get' | 'Create'
    // Remove local formState and formError, use Redux for modal state
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [childViewActive, setChildViewActive] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySource, setCopySource] = useState(null);
    const [copyName, setCopyName] = useState('');
    const [copyError, setCopyError] = useState('');
    const [isCopying, setIsCopying] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);

    // Modal handlers
    const handleEdit = useCallback((row) => {
        setSelectedPlan(row);
        setChildViewActive(true);
        setPlansAction('Create');
        setSelectedTab(0);
    }, []);

    const handleCopy = useCallback((row) => {
        setCopySource(row);
        setCopyName(`Copy of ${row.name}`);
        setCopyError('');
        setCopyModalOpen(true);
    }, []);

    // Columns for AGGridMain
    const columns = useMemo(
        () => [
            {
                headerName: 'Wholesaler',
                field: 'wholesaler',
                hide: currentScope !== 80,
            },
            {
                headerName: 'Partner',
                field: 'partner',
                hide: currentScope < 60,
            },
            { headerName: 'Plan', field: 'name', sortable: true },
            { headerName: 'Description', field: 'description' },
            {
                headerName: 'Country',
                field: 'country',
                hide: true,
            },
            {
                headerName: 'Prefix',
                field: 'countryPrefix',
                hide: true,
            },
            {
                headerName: 'International Rate Card',
                field: 'internationalRateList',
                hide: true,
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => {
                    const actionItems = [
                        {
                            label: 'Edit',
                            onClick: () => handleEdit(params.data),
                            icon: EditIcon,
                        },
                        {
                            label: 'Copy',
                            onClick: () => handleCopy(params.data),
                            icon: CopyIcon,
                        },
                    ];

                    return <ActionMenu actionItems={actionItems} />;
                },
            },
        ],
        [currentScope, handleEdit, handleCopy],
    );

    // Check if entity is selected based on scope
    const hasRequiredEntity =
        (currentScope === 40 && currentPartner) ||
        (currentScope === 60 && currentWholesaler) ||
        currentScope === 80; // Global scope doesn't require selection

    if (!hasRequiredEntity) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>
                    No Partner / Company / Wholesaler Selected
                </AlertTitle>
                <Text>Please select a wholesaler and a partner / company.</Text>
            </Alert>
        );
    }

    // Modal handlers
    const handleCreate = () => {
        dispatch({
            type: 'CHANGE_MODAL_STATE',
            payload: {
                partnerID: '',
                wholesalerID: '',
                isActive: true,
            },
        });
        dispatch({ type: 'CHANGE_MODAL_STATE_ERROR', payload: {} });
        setPlansAction('Create');
        setSelectedPlan(null);
        setChildViewActive(false);
    };

    // Remove validateForm and handleFormSave, PlanDetails will handle via Redux modal state

    // Render child dashboard view if active
    if (childViewActive && selectedPlan && plansAction === 'Create') {
        // Child dashboard tabs: pass full plan to edit form, only id to other tabs (match previous pattern)
        const tabs = [
            {
                label: 'Details',
                component: (
                    <PlanDetails
                        key={selectedPlan?.id}
                        initialData={selectedPlan}
                        isEdit
                        setPlansAction={setPlansAction}
                        onSuccess={refetchPlans}
                    />
                ),
            },
            {
                label: 'Calling Rates',
                component: <CallingRates child={selectedPlan} />,
            },
            {
                label: 'Call Inclusions',
                component: <CallInclusions child={selectedPlan} />,
            },
            {
                label: 'Number Block Rates',
                component: <NumberBlockRates child={selectedPlan} />,
            },
            {
                label: 'Item Code Mappings',
                component: <ItemCodeMapping child={selectedPlan} />,
            },
        ];

        return (
            <>
                <nav
                    style={{
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <span
                        style={{
                            fontWeight: 'bold',
                            marginRight: 8,
                            cursor: 'pointer',
                            color: '#007bff',
                        }}
                        onClick={() => setPlansAction('Get')}>
                        Plans
                    </span>
                    <span
                        style={{
                            margin: '0 8px',
                            fontFamily: 'Inter',
                            fontSize: '14px',
                            fontWeight: 'normal',
                            color: darkMode ? '#FFFFFF' : '#262626',
                        }}>
                        &gt;
                    </span>
                    <BreadcrumbEntityDropdown
                        items={plans}
                        selectedId={selectedPlan?.id}
                        onSelect={setSelectedPlan}
                        darkMode={darkMode}
                        placeholder="Select Plan"
                        width="380px"
                    />
                </nav>
                <Tabs
                    key={selectedPlan?.id}
                    index={selectedTab}
                    onChange={setSelectedTab}>
                    <TabList borderBottom="none" mb={2}>
                        {tabs.map((tab, idx) => (
                            <Tab
                                sx={{
                                    ...customTabStylesCombineTheme,
                                    color: darkMode
                                        ? 'rgba(255, 255, 255)'
                                        : 'inherit',
                                }}
                                _selected={{
                                    ...selectedTabStylesCombineTheme,
                                }}
                                key={idx}>
                                {tab.label}
                            </Tab>
                        ))}
                    </TabList>
                    <TabPanels>
                        {tabs.map((tab, idx) => (
                            <TabPanel padding={0} key={idx}>
                                {tab.component}
                            </TabPanel>
                        ))}
                    </TabPanels>
                </Tabs>
            </>
        );
    }

    // Render main plans table or create form based on plansAction
    if (plansAction === 'Create') {
        return (
            <>
                <nav
                    style={{
                        marginBottom: 16,
                        display: 'flex',
                        alignItems: 'center',
                    }}>
                    <span
                        style={{
                            fontWeight: 'bold',
                            marginRight: 8,
                            cursor: 'pointer',
                            color: '#007bff',
                        }}
                        onClick={() => setPlansAction('Get')}>
                        Plans
                    </span>
                    <span style={{ margin: '0 8px' }}>&gt;</span>
                    <span>Add Plan</span>
                </nav>
                {/* <Box maxW="2xl" mx="auto" p={6} borderWidth={1} borderRadius={8} boxShadow="md"> */}
                <PlanDetails
                    initialData={{
                        isActive: true,
                    }}
                    isEdit={false}
                    setPlansAction={setPlansAction}
                />
                {/* </Box> */}
            </>
        );
    }

    return (
        <>
            {plansAction === 'Get' && (
                <>
                    <Text
                        sx={{
                            fontSize: '20',
                            fontWeight: '500',
                            marginBottom: '15px',
                        }}>
                        Plans
                    </Text>
                    <AGGridMain
                        gridRef={tableRef}
                        titleName="Plans"
                        tableData={{ columns, data: plans }}
                        isLoading={isLoading}
                        isFetching={isFetching}
                        refetch={refetchPlans}
                        isSaveButtonEnabled
                        extraComponents={
                            <div style={{ paddingLeft: '20px' }}>
                                <HStack spacing={2}>
                                    <Tooltip
                                        label="Add Plan"
                                        placement="bottom"
                                        hasArrow
                                        portalProps={{
                                            containerRef: undefined,
                                        }}>
                                        <div
                                            style={{
                                                position: 'relative',
                                                display: 'inline-block',
                                            }}>
                                            {' '}
                                            <Button
                                                size="sm"
                                                width="100px"
                                                onClick={handleCreate}>
                                                + Add Plan
                                            </Button>
                                        </div>
                                    </Tooltip>
                                </HStack>
                            </div>
                        }
                    />
                </>
            )}
            {error && <Box color="red.500">{error}</Box>}
            {/* Modal for copy */}
            <ModalCopy
                type="Plan"
                name={copySource?.name || ''}
                isOpen={copyModalOpen}
                handleClose={() => {
                    setCopyModalOpen(false);
                    setCopyError('');
                }}
                handleSubmit={async () => {
                    if (!copyName || copyName.length < 1) {
                        setCopyError('Name is required');
                        return;
                    }
                    try {
                        setIsCopying(true);
                        // First, call the copy endpoint to get the full plan data
                        const copyRes = await axios.post(
                            `/plan/${copySource.id}/copy`,
                        );
                        const copiedPlan = copyRes.data;

                        // Then, create a new plan with the copied data and new name
                        const res = await axios.put('/plan/', {
                            ...copiedPlan,
                            name: copyName,
                            id: copiedPlan.id,
                        });

                        setCopyModalOpen(false);
                        refetchPlans();
                        // Move straight into edit of the new plan
                        const newPlan = res.data;
                        setSelectedPlan({
                            ...newPlan,
                            wholesaler:
                                newPlan.wholesaler?.length > 0
                                    ? newPlan.wholesaler
                                    : config.notAllocated.table,
                            partner:
                                newPlan.partner?.length > 0
                                    ? newPlan.partner
                                    : config.notAllocated.table,
                        });
                        setChildViewActive(true);
                        setPlansAction('Create');
                        setSelectedTab(0);
                    } catch (err) {
                        setCopyError('Failed to copy plan');
                    } finally {
                        setIsCopying(false);
                    }
                }}
                closeWalkthroughId=""
                deleteWalkthroughId=""
                isDisabled={false}
                isSaving={isCopying}
                isLoading={false}>
                <NameField
                    id="copyName"
                    name="copyName"
                    value={copyName}
                    onChange={(e) => setCopyName(e.target.value)}
                    placeholder="Enter new plan name"
                    width="100%"
                    darkMode={darkMode}
                />
                {copyError && (
                    <Box color="red.500" mt={2}>
                        {copyError}
                    </Box>
                )}
            </ModalCopy>
        </>
    );
};

export default Plans;
